"use client";

import { useEffect, useRef, ReactNode } from "react";
import { usePathname } from "next/navigation";
import { useAppSelector } from "@/app/store/hooks";
import { useLocale } from "@/app/providers/IntlProvider";
import { getAttribution, persistAttribution } from "@/lib/analytics/attribution";
import { detectBrowser, detectDevice } from "@/lib/analytics/device";
import { setAnalyticsLocale } from "@/lib/analytics/page";
import { getOrCreateSessionUUID, safeSessionGet, safeSessionSet } from "@/lib/analytics/session";
import { trackEvent } from "@/lib/analytics/track";
import { usePageAnalytics } from "@/lib/analytics/usePageAnalytics";

// Kept here so existing `@/app/providers/ObservabilityProvider` imports keep working.
export { trackEvent, trackCtaClick } from "@/lib/analytics/track";
export { getSessionUUID } from "@/lib/analytics/session";

const OBS_SENT_KEY = "flots-obs-sent";

type ObservabilityProviderProps = {
  children: ReactNode;
};

/**
 * Website analytics root (see ADR 0075, website analytics contract).
 *
 * - Computes the first-touch attribution on the first page of the visit (memory only until
 *   the visitor accepts cookies, then saved for the tab).
 * - Sends `connect_landing` once per consented session, with the attribution.
 * - Mounts the page-level events (page views, sections, scroll depth) and the flag-gated
 *   anonymous audience counter.
 */
export function ObservabilityProvider({ children }: ObservabilityProviderProps) {
  const consentStatus = useAppSelector((state) => state.cookieConsent.status);
  const { locale } = useLocale();
  const pathname = usePathname() ?? "/";
  const hasFired = useRef(false);

  useEffect(() => {
    setAnalyticsLocale(locale);
  }, [locale]);

  // First page of the visit: freeze the attribution before any client-side navigation.
  useEffect(() => {
    getAttribution();
  }, []);

  useEffect(() => {
    // Once per session, as soon as consent is accepted (on mount when accepted on a previous
    // visit, or right after the visitor accepts the banner).
    if (consentStatus !== "accepted") return;
    persistAttribution();
    if (hasFired.current) return;
    if (safeSessionGet(OBS_SENT_KEY)) return;
    if (!getOrCreateSessionUUID()) return; // crypto.randomUUID or storage unavailable

    hasFired.current = true;
    const attribution = getAttribution();
    void trackEvent("connect_landing", {
      landingPath: attribution?.landingPath,
      locale,
      referrerHost: attribution?.referrerHost,
      source: attribution?.source,
      medium: attribution?.medium,
      campaign: attribution?.campaign,
      content: attribution?.content,
      term: attribution?.term,
      context: attribution?.context,
      device: detectDevice(),
      browser: detectBrowser(),
    });
    safeSessionSet(OBS_SENT_KEY, "true");
  }, [consentStatus, locale]);

  usePageAnalytics(pathname, consentStatus);

  return <>{children}</>;
}
